import React, { useState } from 'react';
import styled from 'styled-components';
import { SimpleButton, ModalElevated, Icon, theme } from '../../../ui';
import { useTranslation } from 'react-i18next';
import EmptyPagePlaceholder from './EmptyPagePlaceholder';
import ModuleTypeSelector from './ModuleTypeSelector';

// Container for the insertion zone
const InsertionContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ $isEmpty }) => ($isEmpty ? 'auto' : '40px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ $isEmpty }) => ($isEmpty ? '40px 20px' : '0')};
`;

/**
 * ModuleInsertionZone - Shows a "+" button to add modules between rows
 *
 * @param {Object} props
 * @param {number} props.position - The position index where module will be inserted
 * @param {Function} props.onInsertModule - Callback when a module type is selected
 * @param {boolean} props.isEmpty - Whether this is an empty page (shows message)
 * @param {string[]} props.allowedModuleTypes - Optional array of module types to allow (e.g., ['task']). If not provided, shows all.
 * @param {string} props.defaultModuleType - If provided, clicking add will directly insert this module type without showing modal
 * @param {string} props.contextType - Optional context type for customized messaging (e.g., 'quiz')
 * @returns {JSX.Element}
 */
const ModuleInsertionZone = ({ position, onInsertModule, isEmpty = false, allowedModuleTypes, defaultModuleType, contextType }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModuleTypeClick = (moduleType) => {
    if (onInsertModule) {
      onInsertModule(moduleType, position);
      setIsModalOpen(false);
    }
  };

  const handleAddButtonClick = () => {
    if (defaultModuleType) {
      handleModuleTypeClick(defaultModuleType);
      return;
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Render the module selection modal
  const renderModal = () => (
    <ModalElevated
      open={isModalOpen}
      onClose={handleCloseModal}
      title={t('editCourse.addModule')}
      subtitle={t('editCourse.selectModuleType')}
      icon={Icon.Add}
      width='600px'
      showActions={false}
    >
      <ModuleTypeSelector onSelectModuleType={handleModuleTypeClick} allowedModuleTypes={allowedModuleTypes} />
    </ModalElevated>
  );

  // Empty page state - use dedicated component
  if (isEmpty) {
    return (
      <InsertionContainer $isEmpty={true}>
        <EmptyPagePlaceholder
          onSelectModuleType={(moduleType) => handleModuleTypeClick(moduleType)}
          allowedModuleTypes={allowedModuleTypes}
          defaultModuleType={defaultModuleType}
          contextType={contextType}
        />
        {renderModal()}
      </InsertionContainer>
    );
  }

  // If defaultModuleType is set, just show simple add button (no modal needed)
  if (defaultModuleType) {
    return <SimpleButton variant='themed' themeColor={theme.colors.NEU_PRIMARY} size='large' icon='Add' noTooltip onClick={handleAddButtonClick} aria-label={t('editCourse.addModule')} />;
  }

  // Non-empty state - add button that opens modal
  return (
    <>
      <SimpleButton
        variant='themed'
        themeColor={theme.colors.NEU_PRIMARY}
        size='large'
        icon='Add'
        noTooltip
        onClick={handleAddButtonClick}
        aria-label={t('editCourse.addModule')}
      />
      {renderModal()}
    </>
  );
};

export default ModuleInsertionZone;
